import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, KeyRound, LogOut, Menu, Plus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth, logoutAllDevices } from '@shared/hooks/useAuth';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAppConfig } from '@shared/app/app-config';
import { PATHS } from '@shared/router/paths';
import { Sidebar } from '@shared/components/layout/sidebar';
import { AppSwitcher } from '@shared/components/layout/app-switcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/avatar';
import { cn } from '@shared/lib/utils';

/** "KJ" from "Karan Jain". */
function initialsOf(value: string): string {
  return value
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * The HEXAUNITY application shell, shared by HRMS, Finance and the Employee
 * Portal: a sidebar from the application's own navigation, a top bar with the
 * application switcher and the account menu, and the routed content.
 *
 * Page-level scroll: the shell is a flowing document. Routes listed in
 * `AppConfig.fullHeightRoutes` lock the shell to the viewport instead, so their
 * table scrolls internally and its horizontal scrollbar stays reachable.
 */
export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const config = useAppConfig();
  const { can } = usePermissions();
  const fillsViewport = (config.fullHeightRoutes ?? []).some((route) =>
    location.pathname.startsWith(route),
  );
  const { user, logout, accounts, activeAccountId, switchAccount, removeAccount, canAddAccount } =
    useAuth();
  const [switching, setSwitching] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const name = user?.employee?.display_name || user?.email || 'User';
  const initials = initialsOf(name);

  const extraLinks = (config.userMenu ?? []).filter(
    (link) => !link.permission || !config.usesPermissions || can(link.permission),
  );

  const changeAccount = async (userId: string) => {
    if (userId === activeAccountId) return setMenuOpen(false);
    setSwitching(userId);
    try {
      const next = await switchAccount(userId);
      setMenuOpen(false);
      toast.success(`Switched to ${next.employee?.display_name || next.email || 'that account'}`);
      navigate(PATHS.root, { replace: true });
    } catch {
      const target = accounts.find((account) => account.userId === userId);
      setMenuOpen(false);
      toast.message('Please sign in to continue with that account');
      navigate(`${PATHS.auth.login}?add=1&email=${encodeURIComponent(target?.email ?? '')}`);
    } finally {
      setSwitching(null);
    }
  };

  const dropAccount = async (userId: string) => {
    const successor = await removeAccount(userId);
    if (userId !== activeAccountId) return;
    setMenuOpen(false);
    if (successor) {
      await changeAccount(successor.userId).catch(() => undefined);
      return;
    }
    navigate(PATHS.auth.login, { replace: true });
  };

  const signOut = async (all = false) => {
    try {
      if (all) await logoutAllDevices();
      else await logout();
      toast.success(all ? 'Signed out from all devices' : 'Signed out');
    } catch {
      toast.error('Signed out on this device');
    } finally {
      navigate(PATHS.auth.login, { replace: true });
    }
  };

  return (
    <div className={cn('flex w-full bg-background', fillsViewport ? 'h-screen' : 'min-h-screen')}>
      <aside
        className={cn(
          'hidden w-[264px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex',
          fillsViewport && 'overflow-y-auto',
        )}
      >
        <Sidebar />
      </aside>
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <aside
            className="flex h-full w-[264px] flex-col overflow-y-auto bg-sidebar text-sidebar-foreground"
            onClick={(event) => event.stopPropagation()}
          >
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-4 md:px-6">
          <button
            type="button"
            className="rounded-md p-1.5 hover:bg-muted lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </button>
          <p className="hidden text-sm font-medium text-muted-foreground lg:block">{config.name}</p>
          <div className="ml-auto flex items-center gap-2">
            <AppSwitcher />
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md p-1 hover:bg-muted"
                  aria-label="Account menu"
                >
                  <span className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {user?.employee?.profile_image ? (
                      <img src={user.employee.profile_image} alt="" className="size-full object-cover" />
                    ) : (
                      initials
                    )}
                  </span>
                  <span className="hidden max-w-[180px] truncate text-sm font-medium sm:block">
                    {name}
                  </span>
                  <ChevronDown className="size-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-64">
                <div className="px-3 py-2">
                  <p className="truncate text-sm font-medium">{name}</p>
                  {user?.email ? (
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                  ) : null}
                </div>
                <div className="my-1 border-t" />
                {extraLinks.map((link) => (
                  <Link
                    key={link.to}
                    onClick={() => setMenuOpen(false)}
                    to={link.to}
                    className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted"
                  >
                    <link.icon className="size-4" />
                    {link.label}
                  </Link>
                ))}
                <Link
                  onClick={() => setMenuOpen(false)}
                  to={PATHS.auth.changePassword}
                  className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted"
                >
                  <KeyRound className="size-4" />
                  Change password
                </Link>

                {accounts.length > 1 || canAddAccount ? (
                  <>
                    <div className="my-1 border-t" />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="gap-2 px-3 py-2 text-sm">
                        <Users className="size-4" />
                        Switch account
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-64" sideOffset={4}>
                        <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Accounts
                        </p>
                        {accounts.map((account) => {
                          const isActive = account.userId === activeAccountId;
                          return (
                            <div key={account.userId} className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => void changeAccount(account.userId)}
                                disabled={switching !== null}
                                className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-60"
                              >
                                <Avatar size="sm">
                                  {isActive && user?.employee?.profile_image ? (
                                    <AvatarImage src={user.employee.profile_image} alt="" />
                                  ) : null}
                                  <AvatarFallback className="text-2xs">
                                    {initialsOf(account.displayName)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="min-w-0 flex-1 truncate">{account.displayName}</span>
                                <Check
                                  className={cn(
                                    'size-4 shrink-0',
                                    isActive ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => void dropAccount(account.userId)}
                                aria-label={`Sign out ${account.displayName}`}
                                className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                              >
                                <X className="size-3.5" />
                              </button>
                            </div>
                          );
                        })}
                        {canAddAccount ? (
                          <>
                            <div className="my-1 border-t" />
                            <Link
                              onClick={() => setMenuOpen(false)}
                              to={`${PATHS.auth.login}?add=1`}
                              className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted"
                            >
                              <Plus className="size-4" />
                              Add account
                            </Link>
                          </>
                        ) : null}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </>
                ) : null}

                <div className="my-1 border-t" />
                <button
                  type="button"
                  onClick={() => void signOut()}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-muted"
                >
                  <LogOut className="size-4" />
                  Sign out
                </button>
                <button
                  type="button"
                  onClick={() => void signOut(true)}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
                >
                  <LogOut className="size-4" />
                  Sign out of all devices
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className={cn('min-w-0', fillsViewport ? 'flex-1 overflow-y-auto' : 'grow')}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className={cn(
                'mx-auto flex w-full min-w-0 max-w-7xl flex-col p-4 sm:p-6',
                fillsViewport && 'min-h-full',
              )}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
